import { CommonModule } from "@angular/common";
import { Component, DestroyRef, EventEmitter, HostListener, inject, Input, Output, signal, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDivider } from "@angular/material/divider";
import { MatFormField, MatPrefix } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { TranslocoPipe } from "@jsverse/transloco";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButton } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";

@Component({
  selector: "loren-filters",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDivider,
    MatFormField,
    MatIcon,
    MatInput,
    MatPrefix,
    TranslocoPipe,
    MatButton,
    MatCheckbox,
  ],
  templateUrl: "./loren-filters.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./loren-filters.component.scss",
})
export class LorenFiltersComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @Input() placeholder: string;
  @Input() initiallySelected: (string | number)[] | string | number;
  @Input() items: { key: string; value: unknown }[];
  @Input() isCalendar: boolean;
  @Input() withNumber: number;
  @Input() showSearch = true;
  @Output() selectionSubmitted = new EventEmitter<Map<unknown, boolean>>();

  searchField = new FormControl("", []);
  searchForm = new FormGroup({
    search: this.searchField,
  });

  filterItems = signal<{ key: string; value: unknown }[]>([]);

  selectionMap = new Map<unknown, boolean>([]);
  numOfChecked = signal<number>(null);

  // Keep the containing mat-menu open while interacting with the filters
  @HostListener("click", ["$event"])
  onClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  ngOnInit() {
    const selected = this.normalizeSelected(this.initiallySelected);
    this.items.forEach((i) =>
      this.selectionMap.set(i.value, selected.includes(i.value as string | number))
    );
    this.filterItems.set(this.items);
    this.searchField.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((v) => this.updateItems(v));
    this.numOfChecked.set(
      Array.from(this.selectionMap.values()).filter((i) => i).length
    );
  }

  private normalizeSelected(
    selected: (string | number)[] | string | number
  ): (string | number)[] {
    if (Array.isArray(selected)) {
      return selected;
    }

    return selected != null ? [selected] : [];
  }

  updateItems(e) {
    if (!e) {
      this.filterItems.set(this.items);
      return;
    }
    this.filterItems.update((value) =>
      value.filter((v) => v.key.toLowerCase().includes(e.toLowerCase()))
    );
  }

  updateSelection(id: number) {
    const value = this.selectionMap.get(id);
    this.selectionMap.set(id, !value);
    this.numOfChecked.set(
      Array.from(this.selectionMap.values()).filter((i) => i).length
    );
  }

  clearSelection() {
    Array.from(this.selectionMap.keys()).forEach((k) =>
      this.selectionMap.set(k, false)
    );
    this.searchField.reset();
    this.numOfChecked.set(0);
    this.selectionSubmitted.emit(this.selectionMap);
  }
}
